import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAqidCookie, getUidCookie } from "src/utils/uid";
import { useBackendFlags } from "src/utils/hooks/useBackendFlags";
import Cookies from "js-cookie";
import { setCurrencyCookie } from "src/utils/currency";
import { setDistanceSystemCookie } from "src/utils/distanceSystem";
import { setTimeFormatCookie } from "src/utils/timeFormat";
import { configs, type ConfigsResponse } from "./v1.6/configs";
import { useFeatureOverride } from "./internal/useFeatureOverride";
import { useExperimentOverride } from "./internal/useExperimentOverride";

const configsKeys = {
  all: ["configs"] as const,
  user: (data: {
    debugFeatures?: string;
    debugExperiments?: string;
    flags?: string;
  }) => [...configsKeys.all, data] as const,
};

export function useConfigsApi<T>(select: (data: ConfigsResponse) => T) {
  const featureOverride = useFeatureOverride();
  const experimentOverride = useExperimentOverride();
  const { backendFlags } = useBackendFlags();
  const queryClient = useQueryClient();

  const queryData = {
    debugFeatures: featureOverride,
    debugExperiments: experimentOverride,
    flags: backendFlags || undefined,
  };

  return useQuery({
    queryKey: configsKeys.user(queryData),
    // Only fetch configs once so we don't get duplicate AQIDs. This means
    // that features enabled by O/D pair will be decided by the first
    // O/D pair of the session.
    staleTime: Infinity,
    select,
    placeholderData(previousData) {
      return previousData;
    },
    async queryFn() {
      const configResponse = await configs({
        ...queryData,
        uid: getUidCookie(),
        aqid: getAqidCookie(),
        referer: document.referrer,
      });
      const incomingQueryKey = configsKeys.user({
        debugFeatures: configResponse.encodedFeatureString,
        debugExperiments: queryData.debugExperiments,
        flags: queryData.flags,
      });
      // Populate the query cache with the response data so that setting
      // the uid/aqid doesn't cause a refetch.
      queryClient.setQueryDefaults(incomingQueryKey, {
        staleTime: Infinity,
      });
      queryClient.setQueryData(incomingQueryKey, configResponse);

      if (configResponse.encodedFeatureString === undefined) {
        Cookies.remove("feature");
      } else {
        Cookies.set("feature", configResponse.encodedFeatureString);
      }

      const isNewUser = !getUidCookie();
      if (isNewUser) {
        Cookies.set("uid", configResponse.uid, {
          expires: 365 * 10,
        });
      }

      const isNewAqid = configResponse.aqid !== getAqidCookie();
      if (isNewAqid) {
        const searchParams = new URLSearchParams(window.location.search);
        const r2rCampaign = searchParams.get("r2r_campaign");
        Cookies.set("r2r_campaign", r2rCampaign ?? "", {
          expires: 365 * 10,
        });

        const gclid = searchParams.get("gclid");
        Cookies.set("gclid", gclid ?? "", {
          expires: 365 * 10,
        });

        Cookies.set("acquisition_url", window.location.href, {
          expires: 365 * 10,
        });

        Cookies.set("aqid", configResponse.aqid, {
          expires: 365 * 10,
        });
      }

      return configResponse;
    },
  });
}

export function useMutateConfigsApi() {
  const featureOverride = useFeatureOverride();
  const experimentOverride = useExperimentOverride();
  const { backendFlags } = useBackendFlags();
  const queryClient = useQueryClient();

  const queryData = {
    debugFeatures: featureOverride,
    debugExperiments: experimentOverride,
    flags: backendFlags || undefined,
  };

  return useMutation({
    async mutationFn(data: Partial<ConfigsResponse>) {
      if (data.currencyCode) {
        setCurrencyCookie(data.currencyCode);
      }
      if (data.distanceUnit) {
        setDistanceSystemCookie(
          data.distanceUnit === "km" ? "metric" : "imperial",
        );
      }
      if (data.clockType) {
        setTimeFormatCookie(data.clockType === "clock12");
      }

      // We don't bother with the API here, we just update the cache, we don't
      // want to fetch configs again because it might reallocate the AQID or change
      // feature/experiment allocation.
      queryClient.setQueryData(
        configsKeys.user(queryData),
        (oldData: ConfigsResponse) => {
          return {
            ...oldData,
            ...data,
          } satisfies ConfigsResponse;
        },
      );
    },
  });
}
